import React, { useState, useEffect } from 'react';
import { DailyReviewSummary, employeeSummary, ProfitCenterSummary as ProfitCenterSummaryInterface, DropdownOption, DailyReviewEmployeeDLSDetails } from './DailyReviewInterfaces';
import ProfitCenterSummary from './ProfitCenterSummary';
import GetStatesForDropdown from './APICalls/GetStatesForDropdown';
import GetProfitCentersByState from './APICalls/GetProfitCentersByState';
import GetDailyReviewSummaryData from './APICalls/GetDailyReviewSummaryData';
import SearchBar from './SearchBar';
import EmployeeOverviewSummary from './EmployeeOverviewSummary';
import Loader from '../../../components/common/Loader';
import './DailyReview.css'; 
import snackBar from '../../../utilities/snackBar/SnackBar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { initializeTimeAdminEmployeeDetails, TimeAdminDetailsProps } from './TimeAdminEmployeeDetail/TimeAdminEmployeeDetail'

//enable UTC and timezone support for dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

function DailyReview() {
    /*  U S E S T A T E S  */
    // page render status
    const [loading, setLoading] = useState<boolean>(true);
    const [searching, setSearching] = useState<boolean>(false);
    const [retreivingDetailData, setRetreivingDetailData] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    // dropdown content
    const [stateDropdown, setStateDropdown] = useState<DropdownOption[]>();
    const [profitCenterDropdown, setProfitCenterDropdown] = useState<DropdownOption[]>();        
    const [date, setDate] = useState<Date>();
    
    // searched params
    const [searchedDate, setSearchedDate] = useState<Date>(new Date());
    const [searchedProfitCenter, setSearchedProfitCenter] = useState<DropdownOption>();

    // api response
    const [dailyReviewSummary, setDailyReviewSummary] = useState<DailyReviewSummary>();

    // 
    const [detailViewEmployeeId, setDetailViewEmployeeId] = useState<number>(-1);
    const [timeAdminDetailsProps, setTimeAdminDetailsProps] = useState<TimeAdminDetailsProps>();
    
    /*  U S E R E F S  */
    let cancelTokenSource: any = React.useRef(null as any);

    /*  A P I   C A L L S   */
    // Gets states for the search dropdown
    const getStatesForDropdown = async () => {
        const response = await GetStatesForDropdown();
        if (response?.success) {
            setLoading(false);
            setStateDropdown(response.response);
        } else {
            showError('There was an error getting states from the server. Please try again.');
            setLoading(false);
        }
    };

    // Once state has been selected, gets all profit centers by state for the dropdown
    const getProfitCentersByState = async (stateId: number) => {
        const response = await GetProfitCentersByState(stateId);
        if (response?.success) {
            setProfitCenterDropdown(response.response);
        } else {
            showError('There was an error getting profit centers from the server. Please try again.');
        }
    };

    // Gets the daily review summary data for specified profit center and date
    const getDailyReviewSummaryData = async (profitCenter: DropdownOption, date: Date | undefined) => { 
        if (profitCenter == null || date == null) {
            showError('There was an error getting Daily Review summary data. Please try again.');
            return
        }
        if (dayjs(date) > dayjs()) {
            showError('The date must not be after todays date.');
            return
        }
        if (searchedDate == date && searchedProfitCenter?.id == profitCenter.id && !error) {
            showError('Results from this search are currently showing.');
            setSearching(false);
            return;
        }
        if (!searching) setSearching(true);

        setDetailViewEmployeeId(-1);

        setSearchedDate(date);
        setSearchedProfitCenter(profitCenter);

        const response = await GetDailyReviewSummaryData(profitCenter.id, date);

        if (response?.success) {
            // @ts-ignore
            setDailyReviewSummary(response.response);
            if (error) setError(false);
        } else {
            showError('There was an error getting Daily Review summary data from the server. Please try again.');
            if (dailyReviewSummary != null ) setDailyReviewSummary(undefined);
            setError(true);
        }      
         
        setSearching(false);
    };
        
    // Get employee detail data and set props from time admin detail component
    const getDailyReviewEmployeeDetailData = async (employeeId: number, employeeName: string, date: Date | undefined, profitCenterID: number | undefined, profitCenterName: string | undefined, handleErrorCallback: any) => {
        if (employeeId == null || date == null || profitCenterID == null) {                        
            showError('There was an error getting the employee summary data. Please try again.');
            handleErrorCallback();
            return
        }        
        setRetreivingDetailData(true);
        const response = await initializeTimeAdminEmployeeDetails(cancelTokenSource, employeeId, employeeName, profitCenterID, profitCenterName || '', date);
        if (!response.success) {
            showError('There was an error getting employee details. Please try again.');
            handleErrorCallback();
            setRetreivingDetailData(false);
            return;
        }
        setTimeAdminDetailsProps(response.response);                        
        setRetreivingDetailData(false);
    };    

    /*  F U N C T I O N S  */
    const formatPercentage = (decimal : number) => {
        if (decimal == null) return '';
        return `${(decimal * 100).toFixed(2)}%`;
    };
    const formatDollar = (amount : number) => {
        if (amount == null) return '';
        return `\$${(amount).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
    };
    const showError = (message: string) => {
        snackBar({ text: message, type: 'error', timeout: 4000 });
    };

    /*  I N I T I A L I Z E R S  */
    const initializeProfitCenterSummary = () => {
        if (dailyReviewSummary == null) {
            const template : ProfitCenterSummaryInterface = {
                DLSCount: 0,
                ClosedDLSCount: 0,
                EmployeeCount: 0,
                EmployeesWithHoursCount: 0,
                ProductionLastCalculatedDate: '',
                ProductionPercentage: 0,
                ProfitCenterID: 0,
                TotalRevenue: 0,
                TotalWages: 0,
                eightWeekTicketCounts: [{ WeekNumber: 0, TicketCount: 0 }],
                sevenDayTicketCounts: [{ ReceivedDate: '', TicketCount: 0, FullDate: new Date() }],
                ticketCategoryCounts: [{ Category: '', ListOrder: 0, TicketCount: 0 }],
                ticketTimeliness: [{ Carryover: 0, Complete: 0, Open: 0, Other: 0, ListOrder: 0, TimeType: '' }]
            };

            return template;
        };

        const summary : ProfitCenterSummaryInterface = {
            DLSCount: dailyReviewSummary.dlsCount.DLSCount,
            ClosedDLSCount: dailyReviewSummary.dlsCount.ClosedDLSCount,
            EmployeeCount: dailyReviewSummary.employeeEnteredHoursCount.EmployeeCount,
            EmployeesWithHoursCount: dailyReviewSummary.employeeEnteredHoursCount.EmployeesWithHoursCount,
            ProductionLastCalculatedDate: dailyReviewSummary.productionSummary.ProductionLastCalculatedDate,
            ProductionPercentage: dailyReviewSummary.productionSummary.ProductionPercentage,
            ProfitCenterID: dailyReviewSummary.productionSummary.ProfitCenterID,
            TotalRevenue: dailyReviewSummary.productionSummary.TotalRevenue,
            TotalWages: dailyReviewSummary.productionSummary.TotalWages,
            eightWeekTicketCounts: dailyReviewSummary.eightWeekTicketCounts,
            sevenDayTicketCounts: dailyReviewSummary.sevenDayTicketCounts,
            ticketCategoryCounts: dailyReviewSummary.ticketCategoryCounts,
            ticketTimeliness: dailyReviewSummary.ticketTimeliness
        };
        return summary;
    };  
    const initializeEmployeeSummaryInterface = () => {
        if (dailyReviewSummary == null || dailyReviewSummary.employeeSummaries == null) {
            const template : employeeSummary = {
                EmployeeID: 0,
                EmployeeName: '',
                FirstSync: null,
                LastSync: null,
                ElapsedTime: '',
                HoursWorkedActual: 0,
                HoursWorkedEstimate: 0,
                TotalWages: 0,
                TotalWagesEstimate: 0,
                RateAmt: 0,
                IsDayApproved: false,
                IsMultipleTimeCodes: false,
                IsDayExported: false,
                IsOnlyRegularHours: false,
                IsAllTimeInProfitCenter: false,
                TotalClosedTickets: 0,
                TotalUtilities: 0,
                TotalRevenue: 0,
                Production: 0,
                TicketsPerHour: 0,
                UtilitiesPerHour: 0,
                IsWagesCountedTowardsProduction: 0 
            };
            return [template];
        }       

        return dailyReviewSummary.employeeSummaries;
    }

    /* FRONTEND workaround only: curretly the api returns a naive datetime string with nothing to say that it is in Mountain Time so the browser doesn't know
       This fix interprets all incoming datetime strings as America/Denver and converts them to the user's local time zone
       ROOT PROBLEM: The root cause (BLL/API returning DateTimeKind.Unspecified) is being tracked separately 
    */

    const formatMountainTimeToLocal = (naiveDateTimeString: string): string => {
        return dayjs.tz(naiveDateTimeString, "America/Denver").local().format('h:mm A');
    }

    /*  U S E E F F E C T S  */
    useEffect(() => {
        getStatesForDropdown();
    }, []);

    return (
        <div className='daily-review-container'>
            {loading && (
                <div>
                    <Loader title="Loading Daily Review" />
                </div>
            )}
            {!loading && (
                <>
                    <div className='daily-review-header-container'>
                        Daily Review
                    </div>
                    <SearchBar
                        stateDropdown={stateDropdown}
                        profitCenterDropdown={profitCenterDropdown}
                        clearProfitCenterDropdown={() => { profitCenterDropdown != null && profitCenterDropdown.length != 0 && setProfitCenterDropdown([]) }}
                        date={date}
                        setDate={setDate}
                        getStatesForDropdown={() => getStatesForDropdown()}
                        getProfitCentersByState={(stateId: number) => getProfitCentersByState(stateId)}
                        getDailyReviewSummaryData={(profitCenter: DropdownOption) => getDailyReviewSummaryData(profitCenter, date)}
                        searching={searching}
                    />
                    {dailyReviewSummary && !error && (
                        <>
                            <ProfitCenterSummary
                                Summary={initializeProfitCenterSummary()}
                                searchedProfitCenter={searchedProfitCenter}
                                searchedDate={searchedDate}
                                formatPercentage={(decimal: number) => formatPercentage(decimal)}
                                formatDollar={(amount: number) => formatDollar(amount)}
                                searching={searching}
                                formatTimeToUsersLocal={(date: string) => formatMountainTimeToLocal(date)}
                            />
                            <EmployeeOverviewSummary
                                OverviewSummary={initializeEmployeeSummaryInterface()}
                                formatPercentage={(decimal: number) => formatPercentage(decimal)}
                                formatDollar={(amount: number) => formatDollar(amount)}
                                getDailyReviewEmployeeDetailData={(employeeId: number, employeeName: string, handleErrorCallback: any) => getDailyReviewEmployeeDetailData(employeeId, employeeName, date, searchedProfitCenter?.id, searchedProfitCenter?.label, handleErrorCallback)}
                                searching={searching}
                                detailViewEmployeeId={detailViewEmployeeId}
                                setDetailViewEmployeeId={((employeeId: number) => setDetailViewEmployeeId(employeeId))}
                                formatTimeToUsersLocal={(date: string) => formatMountainTimeToLocal(date)}
                                timeAdminDetailsProps={timeAdminDetailsProps}
                                clearTimeAdminDetailsProps={() => setTimeAdminDetailsProps(undefined)}
                                retreivingDetailData={retreivingDetailData}
                            />                
                        </>
                    )}
                    {!dailyReviewSummary && !error && (
                        <div className='daily-review-page-icon-container'>
                            <span className="material-icons daily-review-page-icon">today</span>
                        </div>
                    )}
                    {error && (
                        <div className='daily-review-page-icon-container daily-review-page-error-icon-wrapper'>
                            <span className="material-icons daily-review-page-icon">today</span>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default DailyReview;